import { useEffect } from 'react';

const SITE_NAME = 'SkinUse';
const DEFAULT_TITLE = 'SkinUse | Your Intelligent Skin & Hair Care Companion';
const DEFAULT_DESCRIPTION = 'Discover personalized skincare and haircare advice, AI-powered recommendations, expert tutorials, and smart product deals. Your journey to radiant skin and healthy hair starts here.';
const DEFAULT_IMAGE = '/favicon-512.png';

/**
 * SEO Component for dynamic meta tags using document API
 */
export default function SEO({
  title = null,
  description,
  image,
  url,
  type = 'website',
  keywords = '',
  article = null,
  noIndex = false,
}) {
  useEffect(() => {
    // Build title
    const pageTitle = title ? String(title) : null;
    const fullTitle = pageTitle ? `${pageTitle} | ${SITE_NAME}` : DEFAULT_TITLE;
    
    // Set document title
    document.title = fullTitle;
    
    // Get or create meta tags
    const setMeta = (name, content, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        if (property) {
          tag.setAttribute('property', name);
        } else {
          tag.setAttribute('name', name);
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    
    const siteUrl = window.location.origin;
    const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
    const imageStr = image || DEFAULT_IMAGE;
    const fullImage = imageStr.startsWith('http') ? imageStr : `${siteUrl}${imageStr}`;
    const descStr = String(description || DEFAULT_DESCRIPTION);
    const truncatedDescription = descStr.length > 160 ? descStr.substring(0, 157) + '...' : descStr;
    const defaultKeywords = 'skincare, haircare, beauty tips, skin health, hair growth, anti-aging';
    const keywordsStr = keywords ? String(keywords) : '';
    const fullKeywords = keywordsStr ? `${keywordsStr}, ${defaultKeywords}` : defaultKeywords;
    
    // Set meta tags
    setMeta('title', fullTitle);
    setMeta('description', truncatedDescription);
    setMeta('keywords', fullKeywords);
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    
    // Open Graph
    setMeta('og:type', type, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', truncatedDescription, true);
    setMeta('og:image', fullImage, true);
    setMeta('og:site_name', SITE_NAME, true);
    
    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:url', fullUrl);
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', truncatedDescription);
    setMeta('twitter:image', fullImage);
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);
    
    // Article meta tags
    if (article) {
      if (article.author) setMeta('article:author', article.author, true);
      if (article.publishedTime) setMeta('article:published_time', article.publishedTime, true);
      if (article.modifiedTime) setMeta('article:modified_time', article.modifiedTime, true);
      if (article.section) setMeta('article:section', article.section, true);
    }
    
  }, [title, description, image, url, type, keywords, article, noIndex]);
  
  return null; // This component doesn't render anything
}

// Pre-defined SEO configs for static pages
export const PAGE_SEO = {
  home: {
    title: null,
    description: 'Discover personalized skincare and haircare advice, AI-powered recommendations, expert tutorials, and smart product deals. Your journey to radiant skin and healthy hair starts here.',
    keywords: 'skincare platform, beauty advice, personalized skincare',
  },
  dailyDrop: {
    title: 'Daily Drop - Latest Beauty News & Tips',
    description: 'Stay updated with the latest skincare and haircare news, trending tips, and breakthrough discoveries. Your daily dose of beauty intelligence.',
    keywords: 'beauty news, skincare tips, daily beauty advice, trending skincare',
  },
  deepDives: {
    title: 'Deep Dives - In-Depth Beauty Articles',
    description: 'Explore comprehensive articles on skincare science, ingredient breakdowns, and expert beauty guides. Deep dive into the world of skincare.',
    keywords: 'skincare articles, beauty guides, ingredient analysis, skincare science',
  },
  videoVault: {
    title: 'Video Vault - Beauty Tutorials & Guides',
    description: 'Watch expert skincare tutorials, makeup guides, and haircare demonstrations from dermatologists and beauty professionals.',
    keywords: 'beauty tutorials, skincare videos, makeup tutorials, haircare guides',
  },
  knowledge: {
    title: 'Knowledge Base - Skincare Encyclopedia',
    description: 'Your comprehensive skincare encyclopedia. Learn about ingredients, routines, skin types, and evidence-based beauty practices.',
    keywords: 'skincare knowledge, beauty encyclopedia, skin education, ingredient guide',
  },
  quiz: {
    title: 'Beauty Quiz - Test Your Skincare Knowledge',
    description: 'Challenge yourself with fun beauty quizzes! Test your skincare and haircare knowledge, earn SkinPoints, and climb the leaderboard.',
    keywords: 'beauty quiz, skincare trivia, test knowledge, earn points',
  },
  leaderboard: {
    title: 'Leaderboard - Top Beauty Enthusiasts',
    description: 'See who leads the SkinPoints rankings! Compete with other beauty enthusiasts by reading, quizzing, and engaging with content.',
    keywords: 'leaderboard, rankings, skinpoints, beauty community',
  },
  products: {
    title: 'Products - Curated Beauty Recommendations',
    description: 'Discover curated skincare and haircare products recommended by experts. Find the perfect products for your skin type and concerns.',
    keywords: 'skincare products, beauty recommendations, product reviews',
  },
  aiAdvisor: {
    title: 'AI Beauty Advisor - Personalized Skincare Help',
    description: 'Get instant, personalized skincare advice from our AI beauty advisor. Ask questions about routines, ingredients, and skin concerns.',
    keywords: 'AI skincare advice, beauty chatbot, personalized recommendations',
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Your personalized beauty dashboard. Track your progress, see recommendations, and manage your skincare journey.',
    noIndex: true,
  },
  profile: {
    title: 'Profile',
    description: 'Manage your SkinUse profile and preferences.',
    noIndex: true,
  },
  login: {
    title: 'Sign In',
    description: 'Sign in to your SkinUse account to access personalized beauty recommendations and track your skincare journey.',
    noIndex: true,
  },
  register: {
    title: 'Create Account',
    description: 'Join SkinUse for free! Get personalized skincare advice, earn SkinPoints, and connect with a community of beauty enthusiasts.',
    keywords: 'sign up, create account, join skinuse',
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'Read our privacy policy to understand how SkinUse collects, uses, and protects your personal information.',
    keywords: 'privacy policy, data protection',
  },
  terms: {
    title: 'Terms of Use',
    description: 'Review the terms and conditions for using the SkinUse platform and services.',
    keywords: 'terms of service, user agreement',
  },
};
